import { LeagueStanding, TournamentResult } from '../models';
import { LeagueRepository, MatchRepository, ParticipantRepository, TournamentRepository } from '../repositories';

export class GetLeagueStandingsUseCase {
  constructor(
    private leagueRepository: LeagueRepository,
    private tournamentRepository: TournamentRepository,
    private participantRepository: ParticipantRepository,
    private matchRepository: MatchRepository
  ) {}

  async execute(leagueId: string): Promise<LeagueStanding[]> {
    const league = await this.leagueRepository.getById(leagueId);
    if (!league) {
      throw new Error('League not found');
    }

    const leagueParticipants = await this.leagueRepository.getParticipants(leagueId);

    // Build standings map by participant name
    const standingsMap = new Map<string, {
      participantUserId?: string;
      totalPoints: number;
      tournamentResults: TournamentResult[];
      tournamentsPlayed: number;
      firstPlaces: number;
      secondPlaces: number;
    }>();

    for (const lp of leagueParticipants) {
      standingsMap.set(lp.name, {
        participantUserId: lp.userId,
        totalPoints: 0,
        tournamentResults: [],
        tournamentsPlayed: 0,
        firstPlaces: 0,
        secondPlaces: 0,
      });
    }

    // Get all league tournaments using the tournament repository
    // We need to fetch tournaments that have league_id = leagueId
    // Since TournamentRepository doesn't have getByLeagueId, we'll use the data from RPC
    const allUserTournaments = await this.tournamentRepository.getAll(1000, 0);
    const leagueTournaments = allUserTournaments.filter(t => t.leagueId === leagueId);

    // For each completed tournament, compute positions and award points
    for (const tournament of leagueTournaments) {
      if (tournament.status !== 'completed') continue;

      const participants = await this.participantRepository.getByTournamentId(tournament.id);
      const matches = await this.matchRepository.getByTournamentId(tournament.id);

      // Compute leaderboard entries (same logic as GetBracketLeaderboardUseCase)
      const entries = participants.map(participant => {
        let wins = 0;
        let losses = 0;
        let draws = 0;
        let points = 0;

        const participantMatches = matches.filter(
          m => m.participant1Id === participant.id || m.participant2Id === participant.id
        );

        participantMatches.forEach(match => {
          if (match.status === 'completed') {
            if (match.winnerId === participant.id) {
              wins++;
              if (tournament.tournamentType === 'round_robin') points += 3;
            } else if (match.winnerId === null) {
              draws++;
              if (tournament.tournamentType === 'round_robin') points += 1;
            } else if (match.winnerId) {
              losses++;
            }
          }
        });

        const totalMatches = wins + losses + draws;
        const winRate = totalMatches > 0 ? (wins / totalMatches) * 100 : 0;

        return { participant, wins, losses, draws, points, winRate };
      });

      // Sort by tournament type
      if (tournament.tournamentType === 'round_robin') {
        entries.sort((a, b) => {
          if (b.points !== a.points) return b.points - a.points;
          if (b.wins !== a.wins) return b.wins - a.wins;
          return b.winRate - a.winRate;
        });
      } else {
        entries.sort((a, b) => {
          if (b.wins !== a.wins) return b.wins - a.wins;
          return b.winRate - a.winRate;
        });
      }

      // Award league points based on position
      entries.forEach((entry, index) => {
        const position = index + 1;
        const leaguePoints = league.pointSystem[index] || 0;

        // Match league participant by userId first, then by name
        let standing = undefined;
        if (entry.participant.userId) {
          for (const [name, s] of standingsMap.entries()) {
            if (s.participantUserId === entry.participant.userId) {
              standing = s;
              break;
            }
          }
        }
        if (!standing) {
          standing = standingsMap.get(entry.participant.name);
        }

        if (standing) {
          standing.totalPoints += leaguePoints;
          standing.tournamentsPlayed++;
          standing.tournamentResults.push({
            tournamentId: tournament.id,
            tournamentName: tournament.name,
            position,
            pointsAwarded: leaguePoints,
          });
          if (position === 1) standing.firstPlaces++;
          if (position === 2) standing.secondPlaces++;
        }
      });
    }

    // Convert to LeagueStanding array and sort
    const standings: LeagueStanding[] = [];
    for (const [name, data] of standingsMap.entries()) {
      standings.push({
        participantName: name,
        participantUserId: data.participantUserId,
        totalPoints: data.totalPoints,
        tournamentResults: data.tournamentResults,
        tournamentsPlayed: data.tournamentsPlayed,
        position: 0,
      });
    }

    // Sort: total points desc, then first places desc, then second places desc, then alphabetical
    standings.sort((a, b) => {
      if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;

      const aData = standingsMap.get(a.participantName)!;
      const bData = standingsMap.get(b.participantName)!;

      if (bData.firstPlaces !== aData.firstPlaces) return bData.firstPlaces - aData.firstPlaces;
      if (bData.secondPlaces !== aData.secondPlaces) return bData.secondPlaces - aData.secondPlaces;

      return a.participantName.localeCompare(b.participantName);
    });

    // Assign positions
    standings.forEach((s, i) => {
      s.position = i + 1;
    });

    return standings;
  }
}
